import { useEffect, useState } from "react";
import { Drawer, Popconfirm, Tooltip, message } from "antd";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import { COMPLIANCE_QUESTIONNAIRES } from "../../pages/care/complianceQuestionnaireRegistry";
import { CareShell, Panel } from "components/care/CareLayout";
import ComplianceQuestionnaireForm from "components/care/ComplianceQuestionnaireForm";

const pageItems = (payload) =>
  Array.isArray(payload)
    ? payload
    : payload?.content || payload?.data || payload?.items || [];

const currentUserId = (user = {}) =>
  user?.id || user?.userId || user?.uid || "";

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not set"
    : date.toLocaleString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

const hasAnyNo = (answers = {}) =>
  Object.values(answers).some((value) => value === false);

const statusTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "COMPLIANT") return "low";
  if (
    normalized === "AT_RISK" ||
    normalized === "NEEDS_REVIEW" ||
    normalized === "INSUFFICIENT_EVIDENCE"
  )
    return "medium";
  if (normalized === "NON_COMPLIANT") return "high";
  return "medium"; // unknown/never evaluated
};

const statusLabel = (status) =>
  ({
    COMPLIANT: "Compliant",
    AT_RISK: "At Risk",
    NON_COMPLIANT: "Needs Attention",
    NEEDS_REVIEW: "Needs Review",
    INSUFFICIENT_EVIDENCE: "Insufficient Evidence",
  })[status] || "Not Evaluated";

const UNVERIFIED_PREFIX = "Unverified: ";

// Wherever the backend puts the findings list. Adjust once you've seen the response.
const findingsFor = (response) => {
  const list =
    response?.findings ??
    response?.results?.findings ??
    response?.evaluation?.findings;
  return Array.isArray(list)
    ? list.filter((item) => typeof item === "string" && item.trim())
    : [];
};

// Items the system couldn't check are kept apart from confirmed problems.
const splitFindings = (items) => ({
  issues: items.filter((item) => !item.startsWith(UNVERIFIED_PREFIX)),
  unverified: items
    .filter((item) => item.startsWith(UNVERIFIED_PREFIX))
    .map((item) => item.slice(UNVERIFIED_PREFIX.length)),
});

export default function PolicyQuestionnairePage({ policyCode }) {
  const config = COMPLIANCE_QUESTIONNAIRES[policyCode];
  const { user } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const actorId = currentUserId(user);
  const workspaceParams = {
    wid: selectedWorkspaceId,
    workspaceId: selectedWorkspaceId,
  };

  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(undefined); // undefined = closed, null = new, object = existing
  const [drawerReadOnly, setDrawerReadOnly] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const loadResponses = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setResponses([]);
      return;
    }
    setLoading(true);
    careService.complianceQuestionnaireResponses
      .list({ ...workspaceParams, policyCode })
      .then((data) => setResponses(pageItems(data)))
      .catch(() => setResponses([]))
      .finally(() => setLoading(false));
  };

  useEffect(loadResponses, [selectedWorkspaceId, workspaceListReady]);

  if (!config) return null;

  const currentId = responses[0]?.id; // list is submittedAt DESC, so index 0 is current

  const openNew = () => {
    setDrawerRecord(null);
    setDrawerReadOnly(false);
  };
  const openView = (response) => {
    setDrawerRecord(response);
    setDrawerReadOnly(true);
  };
  const openEdit = (response) => {
    setDrawerRecord(response);
    setDrawerReadOnly(false);
  };
  const closeDrawer = () => setDrawerRecord(undefined);

  const handleSaved = () => {
    closeDrawer();
    loadResponses();
  };

  const deleteResponse = async (response) => {
    setDeletingId(response.id);
    try {
      await careService.complianceQuestionnaireResponses.delete(response.id, {
        actorId,
        ...workspaceParams,
      });
      setResponses((current) =>
        current.filter((item) => item.id !== response.id),
      );
      message.success("Response deleted.");
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to delete response.",
      );
    } finally {
      setDeletingId("");
    }
  };

  const currentResponse = responses[0]; // list is submittedAt DESC, index 0 is current
  const currentStatus = currentResponse?.status;
  const needsAttention = [
    "AT_RISK",
    "NON_COMPLIANT",
    "NEEDS_REVIEW",
    "INSUFFICIENT_EVIDENCE",
  ].includes(String(currentStatus || "").toUpperCase());

  const { issues, unverified } = splitFindings(findingsFor(currentResponse));
  const hasListedItems = issues.length + unverified.length > 0;
  // For "Needs Attention" the list replaces the summary, which would only repeat the first item.
  const showSummary = !(
    hasListedItems &&
    String(currentStatus || "").toUpperCase() === "NON_COMPLIANT"
  );

  return (
    <CareShell className="dashboard-content app-page-content emergency-evacuation-drills-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{config.label}</h1>
          {/* <p>{config.reference}</p> */}
          <p>{config.description}</p>
        </div>
        <button
          className="mini-btn emergency-evacuation-schedule-button"
          type="button"
          onClick={openNew}
        >
          <i className={`ti ${config.icon}`} /> Log Compliance Check
        </button>
      </div>

      {!loading && currentResponse && needsAttention ? (
        <div
          className={`compliance-status-notice compliance-status-notice--${statusTone(currentStatus)}`}
        >
          <i
            className={`ti ${statusTone(currentStatus) === "high" ? "ti-alert-triangle" : "ti-alert-circle"}`}
          />
          {/* <div>
            <strong>{statusLabel(currentStatus)}</strong>
            <p>
              {currentResponse.summary ||
                "This policy currently has an unresolved compliance issue."}
            </p>
          </div> */}
          <div>
            <strong>{statusLabel(currentStatus)}</strong>
            {showSummary ? (
              <p>
                {currentResponse.summary ||
                  "This policy currently has an unresolved compliance issue."}
              </p>
            ) : (
              <p>{issues.length + unverified.length} item(s) need attention.</p>
            )}
            {issues.length ? (
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {issues.map((item, index) => (
                  <li key={`issue-${index}`}>{item}</li>
                ))}
              </ul>
            ) : null}
            {unverified.length ? (
              <>
                <p style={{ margin: "12px 0 0" }}>
                  <strong>Could not be verified</strong>
                </p>
                <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                  {unverified.map((item, index) => (
                    <li key={`unverified-${index}`}>{item}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <Panel title="Questionnaire History">
        <div className="medication-table-wrap">
          <table className="medication-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Submitted by</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    Loading history...
                  </td>
                </tr>
              ) : null}
              {!loading && !responses.length ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    No questionnaire submitted yet.
                  </td>
                </tr>
              ) : null}
              {!loading &&
                responses.map((response) => {
                  const flagged = hasAnyNo(response.answers);
                  const isCurrent = response.id === currentId;
                  return (
                    <tr key={response.id}>
                      <td>{formatDateTime(response.submittedAt)}</td>
                      <td>{response.submittedBy || "-"}</td>
                      <td>
                        <em className={`pill ${statusTone(response.status)}`}>
                          {statusLabel(response.status)}
                        </em>
                        {isCurrent ? (
                          <small style={{ marginLeft: 8 }}>Current</small>
                        ) : null}
                      </td>
                      <td>
                        <div className="medication-row-actions">
                          <Tooltip title="View">
                            <button
                              className="resident-icon-button"
                              type="button"
                              onClick={() => openView(response)}
                            >
                              <i className="ti ti-eye" />
                            </button>
                          </Tooltip>
                          {isCurrent ? (
                            <Tooltip title="Edit">
                              <button
                                className="resident-icon-button"
                                type="button"
                                onClick={() => openEdit(response)}
                              >
                                <i className="ti ti-edit" />
                              </button>
                            </Tooltip>
                          ) : null}
                          <Popconfirm
                            title="Delete this response?"
                            okText="Delete"
                            okButtonProps={{
                              danger: true,
                              loading: deletingId === response.id,
                            }}
                            onConfirm={() => deleteResponse(response)}
                          >
                            <Tooltip title="Delete">
                              <button
                                className="resident-icon-button danger"
                                type="button"
                              >
                                <i className="ti ti-trash" />
                              </button>
                            </Tooltip>
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Drawer
        className="assessment-drawer medication-add-drawer emergency-evacuation-drill-drawer"
        destroyOnHidden
        onClose={closeDrawer}
        open={drawerRecord !== undefined}
        title={
          drawerReadOnly
            ? `${config.label} — Details`
            : drawerRecord
              ? `Edit ${config.label}`
              : config.label
        }
        size="min(680px, 96vw)"
      >
        {drawerRecord !== undefined ? (
          <ComplianceQuestionnaireForm
            policyCode={policyCode}
            workspaceId={selectedWorkspaceId}
            actorId={actorId}
            user={user}
            record={drawerRecord}
            readOnly={drawerReadOnly}
            onSaved={handleSaved}
            onCancel={closeDrawer}
          />
        ) : null}
      </Drawer>
    </CareShell>
  );
}
